import { findLastIndexWhere } from "./binarySearch";
import type { TimeRange } from "./coordinateConversion";
import { findValueAtOrBefore, hasValueAt } from "./findValueAtOrBefore";

export type RealPoint = {
	index: number;
	timestamp: number;
	value: number;
};

export type ResolvedTooltipValue = {
	value: number | null;
	exact?: boolean;
	approximateAt?: number;
};

export type NearestPointEntry = {
	seriesIndex: number;
	visible: boolean;
	data: [number, number | null][];
	realAt: ReadonlySet<number>;
	realPoints: RealPoint[];
};

export type NearestPointLookup = Record<number, NearestPointEntry>;

export type TooltipValueRequest = {
	rawData: [number, number | null][];
	entry: NearestPointEntry | undefined;
	time: number;
	window: TimeRange;
};

export const collectRealPoints = (
	data: [number, number | null][],
	realAt: ReadonlySet<number>,
): RealPoint[] => {
	const points: RealPoint[] = [];

	data.forEach(([timestamp, value], index) => {
		if (value !== null && realAt.has(timestamp)) {
			points.push({ index, timestamp, value });
		}
	});

	return points;
};

export const buildNearestPointLookup = (
	processedData: { analogSeries: any[]; digitalSeries: any[] },
	displaySeries: { analog: any[]; digital: any[] },
): NearestPointLookup => {
	const lookup: NearestPointLookup = {};

	const add = (list: any[], display: any[], seriesOffset: number) => {
		list.forEach((s, idx) => {
			const entry = display[idx];
			if (!entry?.realAt) return;

			lookup[s.tagId] = {
				seriesIndex: seriesOffset + idx,
				visible: s.visible,
				data: entry.data,
				realAt: entry.realAt,
				realPoints: collectRealPoints(entry.data, entry.realAt),
			};
		});
	};

	add(processedData.analogSeries, displaySeries.analog, 0);
	add(
		processedData.digitalSeries,
		displaySeries.digital,
		processedData.analogSeries.length,
	);

	return lookup;
};

export const isBlankAt = (
	data: [number, number | null][],
	time: number,
): boolean => {
	if (data.length === 0) return true;

	const index = findLastIndexWhere(data, (point) => point[0] <= time);
	if (index < 0) return true;
	if (data[index][1] === null) return true;

	return index === data.length - 1 && time > data[index][0];
};

export const findNearestRealPoint = (
	points: RealPoint[],
	time: number,
	window: TimeRange,
): RealPoint | null => {
	const withinWindow = (point: RealPoint | undefined) =>
		point && point.timestamp >= window.start && point.timestamp <= window.end
			? point
			: null;

	const beforeIndex = findLastIndexWhere(
		points,
		(point) => point.timestamp <= time,
	);

	const left = withinWindow(beforeIndex >= 0 ? points[beforeIndex] : undefined);
	const right = withinWindow(points[beforeIndex + 1]);

	if (!left) return right;
	if (!right) return left;

	return time - left.timestamp <= right.timestamp - time ? left : right;
};

export const resolveTooltipValue = ({
	rawData,
	entry,
	time,
	window,
}: TooltipValueRequest): ResolvedTooltipValue => {
	if (entry?.realAt.has(time) && hasValueAt(rawData, time)) {
		return { value: findValueAtOrBefore(rawData, time), exact: true };
	}

	if (!entry || !isBlankAt(entry.data, time)) {
		return { value: findValueAtOrBefore(rawData, time) };
	}

	const nearest = findNearestRealPoint(entry.realPoints, time, window);
	if (!nearest) return { value: null };

	return { value: nearest.value, approximateAt: nearest.timestamp };
};
